import { NotImplementedError } from "../../../../errors";
import { parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
import { GEO_MAP_QUERY } from "./query";
/**
 * A metric is only meaningful when the backend returns a finite number.
 * Anything else becomes null so the UI shows "no data" rather than a
 * misleading 0.
 */
const toMetric = (value) => typeof value === "number" && Number.isFinite(value) ? value : null;
/**
 * Parses a Looker `location` value into coordinates. Accepts the shapes the
 * field can take — a "lat,lng" string, a [lat, lng] tuple, or an object with
 * lat/lng-style keys — and returns null for anything unparseable or out of
 * range. Never throws, so one bad row can't fail the whole response.
 */
const parseLocation = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    let lat;
    let lng;
    if (typeof value === "string") {
        const [latRaw, lngRaw] = value.split(",");
        lat = Number(latRaw);
        lng = Number(lngRaw);
    }
    else if (Array.isArray(value) && value.length >= 2) {
        lat = Number(value[0]);
        lng = Number(value[1]);
    }
    else if (typeof value === "object") {
        const obj = value;
        lat = Number(obj.latitude ?? obj.lat ?? obj.y);
        lng = Number(obj.longitude ?? obj.lng ?? obj.lon ?? obj.x);
    }
    else {
        return null;
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return null;
    }
    return { lat, lng };
};
/**
 * Typed handler for the Overview dashboard Geo Targeting map widget. Owns the
 * LookerML query construction, "lat,lng" parsing, response typing, and caching
 * so the frontend hook never builds a raw query envelope.
 */
export default class LookerOverviewGeoMap extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview-geo-map");
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const queryBody = {
            ...GEO_MAP_QUERY,
            filters: {
                "explore_overview.flt_date_range": parseDateFilter(dateFilter)
            }
        };
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (lookbackFilter?.value !== undefined && lookbackFilter.value !== null) {
            queryBody.filters["explore_overview.pmt_lookback_window"] = String(lookbackFilter.value);
        }
        // Media type selection maps to product_group (one value or comma-joined).
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        const mediaType = mediaTypeFilter?.value;
        if (mediaType && mediaType.length) {
            queryBody.filters["explore_overview.product_group"] = Array.isArray(mediaType)
                ? mediaType.join(",")
                : mediaType;
        }
        // Retargeting-only toggle. When on, filter to the inst-level retargeting
        // dimension (one row per vendor+inst, so it can't fan out). Off = no filter
        // (all delivery). Deliberately NOT adgroups.is_retargeting, whose join fans
        // out (~2.1x impressions, ~4.8x clicks).
        const retargetingFilter = filters.where?.find((filter) => filter.field === "retargeting");
        if (retargetingFilter?.value === true) {
            queryBody.filters["adgroup_rtg_by_inst.is_retargeting"] = "Yes";
        }
        // Fail closed: the map is partner-scoped exclusively by advertiser +
        // campaign. Without both the query would aggregate across every partner, so
        // we refuse to run it (matches the sibling overview handlers).
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (!advertiserIds?.length) {
            throw new Error("Advertiser ids filter is required");
        }
        queryBody.filters["advertisers.advertiser_id"] = advertiserIds.join(",");
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (!campaignIds?.length) {
            throw new Error("Campaign ids filter is required");
        }
        queryBody.filters["campaigns.campaign_id"] = campaignIds.join(",");
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: this.formatResponse(data),
                queryMeta
            }
        ])
            .catch((e) => {
            console.error(e);
            throw new Error("Failed to fetch overview dashboard geo map");
        }));
    }
    formatResponse(data) {
        return data
            .map((row) => {
            const coords = parseLocation(row["explore_overview.location"]);
            if (!coords) {
                return null;
            }
            return {
                id: `${coords.lat},${coords.lng}`,
                lat: coords.lat,
                lng: coords.lng,
                city: row["explore_overview.city"] ?? null,
                postal: row["explore_overview.postal"] ?? null,
                impressions: toMetric(row["explore_overview.total_impressions"]),
                clicks: toMetric(row["explore_overview.total_clicks"]),
                vcr: toMetric(row["explore_overview.vcr"]),
                selectedMetric: toMetric(row["explore_overview.selected_metric"])
            };
        })
            .filter((point) => point !== null);
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=GeoMap.js.map